import { useState, type ChangeEvent } from 'react';
import { Input, Modal } from 'antd';
import type { Contact, ContactDraft, ContactDraftErrors } from '../../../types/InfrastructureManagerTypes';
import { CONTACT_FIELDS, EMPTY_CONTACT_DRAFT, PHONE_DIGITS, REQUIRED_FIELDS } from './constants';
import { toDraft, toPhoneDigits, trimDraft, validateDraft } from './contact.utils';
import styles from './Contacts.module.scss';

interface ContactFormModalProps {
  open: boolean;
  contact: Contact | null;
  isSaving: boolean;
  onSubmit: (fields: ContactDraft) => void;
  onClose: () => void;
}

export const ContactFormModal = ({ open, contact, isSaving, onSubmit, onClose }: ContactFormModalProps) => {
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_CONTACT_DRAFT);
  const [errors, setErrors] = useState<ContactDraftErrors>({});
  const [wasOpen, setWasOpen] = useState(open);
  const isEditing = contact !== null;

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(contact ? toDraft(contact) : EMPTY_CONTACT_DRAFT);
      setErrors({});
    }
  }

  const handleDraftChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const field = event.target.name as keyof ContactDraft;
    const value = field === 'phone' ? toPhoneDigits(event.target.value).slice(0, PHONE_DIGITS) : event.target.value;
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = () => {
    const fields = trimDraft(draft);
    const nextErrors = validateDraft(fields);
    if (Object.keys(nextErrors).length) setErrors(nextErrors);
    else onSubmit(fields);
  };

  return (
    <Modal
      className={styles.contactModal}
      destroyOnClose
      open={open}
      title={isEditing ? 'EDIT CONTACT' : 'CREATE NEW CONTACT'}
      okText={isEditing ? 'Save Contact' : 'Create Contact'}
      cancelText="Cancel"
      confirmLoading={isSaving}
      onOk={handleSubmit}
      onCancel={onClose}
    >
      <div className={styles.contactForm}>
        {CONTACT_FIELDS.map((field) => (
          <label key={field} className={styles.modalField}>
            <span>
              {field === 'phone' ? 'PHONE NUMBER' : field.toUpperCase()}
              {REQUIRED_FIELDS.has(field) ? <span className={styles.requiredMark}>*</span> : null}
            </span>
            {field === 'notes' ? (
              <Input.TextArea
                rows={2}
                className={`${styles.contactInput} ${styles.notesInput} ${errors[field] ? styles.inputError : ''}`}
                name={field}
                value={draft[field]}
                onChange={handleDraftChange}
              />
            ) : (
              <Input
                className={`${styles.contactInput} ${errors[field] ? styles.inputError : ''}`}
                name={field}
                value={draft[field]}
                inputMode={field === 'phone' ? 'numeric' : undefined}
                onChange={handleDraftChange}
              />
            )}
            {errors[field] ? <small className={styles.validationError}>{errors[field]}</small> : null}
          </label>
        ))}
      </div>
    </Modal>
  );
};
